import axios from "axios";
import { MAIN_API } from "./stepoutAPI";

/**
 * Verify and refresh token if invalid
 * @param {string} refreshToken - Refresh token to verify and get new access token
 * @returns {Promise<{valid: boolean, newToken?: string, logout?: boolean}>}
 */
export const verifyToken = async (refreshToken) => {
  try {
    const response = await axios({
      method: "POST",
      url: `${MAIN_API}verify-token-analyst/`,
      data: {
        token: refreshToken,
      },
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200) {
      return {
        valid: true,
        newToken: response?.data?.token || refreshToken,
      };
    }
  } catch (error) {
    if (error.response?.status === 403) {
      // Check if the error message is INVALID_TOKEN
      if (error.response?.data?.msg === "INVALID_TOKEN") {
        return {
          valid: false,
          newToken: null,
          logout: true, // Signal to logout the user
        };
      }
      return {
        valid: false,
        newToken: null,
        logout: false,
      };
    }
    throw error;
  }
};

/**
 * Setup axios interceptor for token refresh
 * This intercepts 403 responses and attempts to refresh the token
 */
export const setupAxiosInterceptor = () => {
  // Response interceptor to handle 403 errors
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Check if error is 403 (Forbidden - Invalid Token)
      if (error.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          // Get refresh token from sessionStorage
          const refreshToken = sessionStorage.getItem("refresh_token");
          const currentToken = sessionStorage.getItem("token");

          if (!refreshToken) {
            // No refresh token available, redirect to login
            sessionStorage.removeItem("token");
            sessionStorage.removeItem("refresh_token");
            window.location.href = "/login";
            return Promise.reject(error);
          }

          // Verify and refresh token using refresh token
          const { valid, newToken, logout } = await verifyToken(refreshToken);

          if (logout) {
            // Token is invalid and user should be logged out
            sessionStorage.removeItem("token");
            sessionStorage.removeItem("refresh_token");
            window.location.href = "/login";
            return Promise.reject(error);
          }

          if (valid && newToken && newToken !== currentToken) {
            // Update token in storage with the new token
            sessionStorage.setItem("token", newToken);

            // Update authorization header with new token
            originalRequest.headers.Authorization = "ANALYST " + newToken;

            // Retry original request with new token
            return axios(originalRequest);
          } else if (valid && newToken === currentToken) {
            // Token validation successful but no new token returned
            // This means token is still valid, retry the request
            return axios(originalRequest);
          } else {
            // Token is invalid and cannot be refreshed
            sessionStorage.removeItem("token");
            sessionStorage.removeItem("refresh_token");
            window.location.href = "/login";
            return Promise.reject(error);
          }
        } catch (refreshError) {
          // Error during token refresh process
          console.error("Token refresh failed:", refreshError);
          sessionStorage.removeItem("token");
          sessionStorage.removeItem("refresh_token");
          window.location.href = "/login";
          return Promise.reject(refreshError);
        }
      }

      // For other errors, just reject
      return Promise.reject(error);
    }
  );
};
